import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    FiAlertCircle,
    FiAward,
    FiBookOpen,
    FiCheckCircle,
    FiChevronDown,
    FiDatabase,
    FiEye,
    FiFileText,
    FiHelpCircle,
    FiLoader,
    FiPauseCircle,
    FiPlay,
    FiPlus,
    FiRefreshCw,
    FiRotateCcw,
    FiSend,
    FiUploadCloud,
    FiXCircle,
} from 'react-icons/fi';
import './QuestionEngine.css';
import {getEngineUsageErrorMessage, getErrorMessage, questionEngineApi, resourcesApi} from '../services/api';
import EngineUsageCard from '../components/EngineUsageCard/EngineUsageCard';
import MathText from '../components/MathText';
import useEngineUsage from '../hooks/useEngineUsage';

const terminalStatuses = ['completed', 'failed', 'cancelled'];
const statusLabels = {
    created: 'ساخته شده',
    extracting: 'استخراج محتوا',
    analyzing_document: 'تحلیل سند',
    defining_assessment: 'طراحی آزمون',
    planning: 'برنامه‌ریزی سؤال‌ها',
    generating: 'تولید سؤال‌ها',
    judging: 'داوری کیفیت',
    waiting_for_user: 'نیازمند پاسخ معلم',
    completed: 'تکمیل شده',
    failed: 'ناموفق',
    cancelled: 'لغو شده',
};

const agentLabels = {
    content_extractor: 'استخراج‌کننده محتوا',
    document_analyst: 'تحلیل سند',
    document_analyzer: 'تحلیل‌گر سند',
    assessment_designer: 'طراح آزمون',
    evidence_planner: 'برنامه‌ریز شواهد',
    question_writer: 'نویسنده سؤال',
    judge: 'داور مستقل',
    question_judge: 'داوری کیفیت',
};

const formatStatus = (status) => statusLabels[status] || (status ? 'در حال پردازش' : 'نامشخص');
const normalizeSessions = (data) => Array.isArray(data) ? data : (data?.sessions || data?.results || []);
const normalizeResources = (data) => Array.isArray(data) ? data : (data?.items || data?.results || []);
const isRunningStatus = (status) => status && !terminalStatuses.includes(status) && status !== 'waiting_for_user';

const workflowSteps = [
    {key: 'extracting', label: 'استخراج'},
    {key: 'analyzing_document', label: 'تحلیل سند'},
    {key: 'defining_assessment', label: 'طراحی آزمون'},
    {key: 'generating', label: 'تولید سؤال'},
    {key: 'judging', label: 'داوری'},
];

const workflowStatusIndexes = {
    created: -1,
    extracting: 0,
    analyzing_document: 1,
    defining_assessment: 2,
    planning: 2,
    generating: 3,
    judging: 4,
    completed: 5,
};

const questionTypeLabels = {
    multiple_choice: 'چندگزینه‌ای',
    multiple_select: 'چندپاسخی',
    true_false: 'درست / نادرست',
    short_answer: 'پاسخ کوتاه',
    essay: 'تشریحی',
    matching: 'تطبیقی',
};

const difficultyLabels = {easy: 'آسان', medium: 'متوسط', hard: 'دشوار'};
const bloomLabels = {
    remember: 'یادآوری', understand: 'درک', apply: 'کاربرد', analyze: 'تحلیل', evaluate: 'ارزیابی', create: 'خلق',
};

const extractionStatusLabels = {
    pending: 'در انتظار استخراج',
    processing: 'در حال استخراج',
    ready: 'آماده',
    failed: 'ناموفق',
};

const clarificationTypeLabels = {
    boolean: 'بله یا خیر',
    integer: 'پاسخ عددی',
    text: 'پاسخ متنی',
    multiple_choice: 'چند انتخابی',
    single_choice: 'تک‌انتخابی',
};

const readableValue = (value) => {
    if (value === true) return 'درست';
    if (value === false) return 'نادرست';
    if (Array.isArray(value)) return value.map(readableValue).filter(Boolean).join('، ');
    if (value && typeof value === 'object') {
        if (value.left !== undefined || value.right !== undefined) return `${value.left || '—'} ← ${value.right || '—'}`;
        return value.message || value.detail || value.label || value.text || '';
    }
    return value === undefined || value === null ? '' : String(value);
};

const formatWarning = (warning) => readableValue(warning) || 'هنگام استخراج سند یک هشدار ثبت شد.';

const getSessionSubtitle = (session) => {
    const title = session.title || '';
    const documentName = session.document?.name || '';
    const stage = session.current_agent
        ? agentLabels[session.current_agent] || 'پردازش'
        : session.status === 'created' ? 'در انتظار شروع پردازش' : formatStatus(session.status);
    return [documentName && documentName !== title ? documentName : '', stage].filter(Boolean).join(' · ');
};

const getSessionErrorText = (session) => {
    if (!session?.error) return '';
    if (typeof session.error === 'string') return session.error;
    const quotaMessage = getEngineUsageErrorMessage({data: session.error, code: session.error.code});
    if (quotaMessage) return quotaMessage;
    return readableValue(session.error) || 'پردازش طراحی با خطا روبه‌رو شد.';
};

const QuestionEngine = () => {
    const [sessions, setSessions] = useState([]);
    const [activeSessionId, setActiveSessionId] = useState('');
    const [activeSession, setActiveSession] = useState(null);
    const [documentFile, setDocumentFile] = useState(null);
    const [sourceMode, setSourceMode] = useState('existing');
    const [resources, setResources] = useState([]);
    const [resourcesLoading, setResourcesLoading] = useState(false);
    const [resourcesLoaded, setResourcesLoaded] = useState(false);
    const [resourceSearch, setResourceSearch] = useState('');
    const [selectedResourceId, setSelectedResourceId] = useState('');
    const [title, setTitle] = useState('');
    const [interfaceLanguage, setInterfaceLanguage] = useState('fa');
    const [preset, setPreset] = useState('balanced');
    const [autoAnswerClarifications, setAutoAnswerClarifications] = useState(false);
    const [answers, setAnswers] = useState({});
    const [rememberIds, setRememberIds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [busyAction, setBusyAction] = useState('');
    const [message, setMessage] = useState(null);
    const [sessionResult, setSessionResult] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const pollTimer = useRef(null);
    const resultsRef = useRef(null);
    const createPanelRef = useRef(null);
    const engineUsage = useEngineUsage(sourceMode === 'existing' ? selectedResourceId : '');
    const presetUnits = preset === 'summative' ? 20 : preset === 'custom' ? 1 : 10;
    const canStartGeneration = Boolean(engineUsage.usage?.exempt || (engineUsage.usage?.allowed
        && !engineUsage.concurrencyBlocked && engineUsage.availableQuestions >= presetUnits));
    const availableResources = useMemo(
        () => resources.filter((resource) => resource.file_url && resource.can_edit === true),
        [resources],
    );
    const filteredResources = useMemo(() => {
        const query = resourceSearch.trim().toLocaleLowerCase('fa');
        if (!query) return availableResources;
        return availableResources.filter((resource) => [
            resource.title,
            resource.original_name,
            resource.subject_title,
            resource.resource_id,
        ].filter(Boolean).join(' ').toLocaleLowerCase('fa').includes(query));
    }, [availableResources, resourceSearch]);
    const hasSource = sourceMode === 'existing' ? Boolean(selectedResourceId) : Boolean(documentFile);

    const loadResources = useCallback(async () => {
        setResourcesLoading(true);
        try {
            const items = normalizeResources(await resourcesApi.list());
            setResources(items);
            setResourcesLoaded(true);
            const selectable = items.filter((resource) => resource.file_url && resource.can_edit === true);
            setSelectedResourceId((current) => selectable.some((resource) => String(resource.resource_id) === current)
                ? current : String(selectable[0]?.resource_id || ''));
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setResourcesLoading(false);
        }
    }, []);

    const clearPoll = () => clearTimeout(pollTimer.current);

    const toggleCreatePanel = () => {
        const nextState = !showCreate;
        setShowCreate(nextState);
        if (nextState) setShowResults(false);
    };

    const selectSession = (sessionId) => {
        setShowCreate(false);
        setActiveSessionId(sessionId);
    };

    const loadSessions = useCallback(async (selectLatest = false) => {
        setIsLoading(true);
        try {
            const items = normalizeSessions(await questionEngineApi.sessions());
            setSessions(items);
            if (!items.length) setShowCreate(true);
            if (selectLatest && items[0]?.id) setActiveSessionId(items[0].id);
            setActiveSessionId((current) => current || items[0]?.id || '');
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setIsLoading(false);
        }
    }, []);

    const pollStatus = useCallback(async (sessionId) => {
        if (!sessionId) return;
        try {
            const session = await questionEngineApi.status(sessionId);
            setActiveSession(session);
            setSessions((current) => {
                const exists = current.some((item) => item.id === session.id);
                return exists
                    ? current.map((item) => item.id === session.id ? {...item, ...session} : item)
                    : [session, ...current];
            });
            if (session.open_clarifications?.length) {
                setAnswers((current) => {
                    const next = {...current};
                    session.open_clarifications.forEach((item) => {
                        if (next[item.id] === undefined) {
                            next[item.id] = item.kind === 'multiple_choice' ? [] : (item.suggested_answer || '');
                        }
                    });
                    return next;
                });
            }
            clearPoll();
            if (isRunningStatus(session.status)) {
                pollTimer.current = setTimeout(() => pollStatus(sessionId), 3000);
            }
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        }
    }, []);

    useEffect(() => {
        loadSessions(true);
        return clearPoll;
    }, [loadSessions]);

    useEffect(() => {
        clearPoll();
        setSessionResult(null);
        setShowResults(false);
        if (activeSessionId) pollStatus(activeSessionId);
        return clearPoll;
    }, [activeSessionId, pollStatus]);

    useEffect(() => {
        if (!showCreate) return undefined;
        const previousOverflow = document.body.style.overflow;
        const handleEscape = (event) => {
            if (event.key === 'Escape' && !isCreating) setShowCreate(false);
        };
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleEscape);
        if (!createPanelRef.current?.contains(document.activeElement)) createPanelRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showCreate, isCreating]);

    useEffect(() => {
        if (showCreate && !resourcesLoaded && !resourcesLoading) loadResources();
    }, [showCreate, resourcesLoaded, resourcesLoading, loadResources]);

    const stats = useMemo(() => ({
        all: sessions.length,
        running: sessions.filter((session) => isRunningStatus(session.status)).length,
        waiting: sessions.filter((session) => session.status === 'waiting_for_user').length,
        completed: sessions.filter((session) => session.status === 'completed').length,
    }), [sessions]);

    const progress = activeSession?.progress || {};
    const openClarifications = activeSession?.open_clarifications || [];
    const questionBank = activeSession?.question_bank || {};
    const pendingApproval = Number(questionBank.pending_approval) || 0;
    const importedQuestions = Number(questionBank.imported) || 0;

    const createSession = async (event) => {
        event.preventDefault();
        if (!hasSource) {
            setMessage({type: 'error', text: 'برای شروع، یک منبع موجود یا فایل جدید انتخاب کنید.'});
            return;
        }
        setIsCreating(true);
        setMessage(null);
        try {
            const session = await questionEngineApi.createSession({
                document: sourceMode === 'upload' ? documentFile : null,
                resourceId: sourceMode === 'existing' ? selectedResourceId : '',
                title: title.trim(),
                preset,
                interface_language: interfaceLanguage,
                auto_answer_clarifications: autoAnswerClarifications,
            });
            setDocumentFile(null);
            setSelectedResourceId('');
            setTitle('');
            setAutoAnswerClarifications(false);
            setAnswers({});
            setRememberIds([]);
            setActiveSessionId(session.id);
            setActiveSession(session);
            setSessions((current) => [session, ...current.filter((item) => item.id !== session.id)]);
            setShowCreate(false);
            setMessage({type: 'success', text: 'فرایند طراحی سؤال ایجاد شد و پردازش آغاز شد.'});
            engineUsage.refresh();
            pollStatus(session.id);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
            if ([403, 429].includes(error?.status)) engineUsage.refresh();
        } finally {
            setIsCreating(false);
        }
    };

    const setAnswer = (clarification, value) => {
        setAnswers((current) => ({...current, [clarification.id]: value}));
    };

    const toggleMultipleAnswer = (clarification, value) => {
        setAnswers((current) => {
            const selected = Array.isArray(current[clarification.id]) ? current[clarification.id] : [];
            return {
                ...current,
                [clarification.id]: selected.includes(value)
                    ? selected.filter((item) => item !== value)
                    : [...selected, value],
            };
        });
    };

    const toggleRemember = (clarificationId) => {
        setRememberIds((current) => current.includes(clarificationId)
            ? current.filter((item) => item !== clarificationId)
            : [...current, clarificationId]);
    };

    const submitAnswers = async (event) => {
        event.preventDefault();
        if (!activeSession?.id) return;
        setBusyAction('answers');
        setMessage(null);
        try {
            await questionEngineApi.answerClarifications(activeSession.id, {
                answers: Object.fromEntries(openClarifications.map((item) => [item.id, answers[item.id]])),
                remember: rememberIds.filter((id) => openClarifications.some((item) => item.id === id)),
            });
            setMessage({type: 'success', text: 'پاسخ‌ها ثبت شد و پردازش ادامه پیدا کرد.'});
            setAnswers({});
            setRememberIds([]);
            pollStatus(activeSession.id);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyAction('');
        }
    };

    const runSessionAction = async (name, action, successText) => {
        if (!activeSession?.id) return;
        setBusyAction(name);
        setMessage(null);
        try {
            await action(activeSession.id);
            setMessage({type: 'success', text: successText});
            pollStatus(activeSession.id);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            engineUsage.refresh();
            setBusyAction('');
        }
    };

    const loadResults = async () => {
        if (!activeSession?.id) return;
        if (sessionResult) {
            setShowResults(true);
            setTimeout(() => resultsRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'}), 0);
            return;
        }
        setBusyAction('results');
        setMessage(null);
        try {
            const result = await questionEngineApi.results(activeSession.id);
            setSessionResult(result);
            setShowResults(true);
            setTimeout(() => resultsRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'}), 0);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyAction('');
        }
    };

    useEffect(() => {
        if (activeSession?.status === 'completed' && !sessionResult && busyAction !== 'results') {
            loadResults();
        }
        // Loading is tied to the selected session; the selection effect clears stale results.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSession?.id, activeSession?.status]);

    useEffect(() => {
        if (terminalStatuses.includes(activeSession?.status)) engineUsage.refresh();
        // Refresh only when a session reaches a terminal lifecycle state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSession?.id, activeSession?.status]);

    const renderQuestion = (question, index) => {
        const correctAnswers = new Set((Array.isArray(question.correct_answer) ? question.correct_answer : [question.correct_answer]).map(String));
        const resolvedAnswer = question.options?.length
            ? question.options
                .filter((option) => correctAnswers.has(String(option.key)))
                .map((option) => `${option.key}. ${option.text}`)
                .join('، ')
            : readableValue(question.correct_answer);
        const score = Number(question.judge_score);
        return (
            <article className="qe-question-card" key={`${question.position || index}-${question.stem || index}`}>
                <header>
                    <div className="qe-question-number">{question.position || index + 1}</div>
                    <div className="qe-question-meta">
                        <span>{questionTypeLabels[question.question_type] || 'سؤال'}</span>
                        <span>{difficultyLabels[question.difficulty] || 'سطح نامشخص'}</span>
                        {question.bloom_level && <span>{bloomLabels[question.bloom_level] || 'مهارت شناختی'}</span>}
                        <span>{question.marks || 1} نمره</span>
                    </div>
                    {Number.isFinite(score) && <div className="qe-judge-score"><FiAward/> امتیاز داور: {score}</div>}
                </header>
                <div className="qe-question-body">
                    <h3><MathText text={question.stem}/></h3>
                    {question.instructions && <p className="qe-question-instructions"><MathText text={question.instructions}/></p>}
                    {!!question.options?.length && (
                        <div className="qe-result-options">
                            {question.options.map((option) => (
                                <div key={option.key} className={correctAnswers.has(String(option.key)) ? 'correct' : ''}>
                                    <b>{option.key}</b><span><MathText text={option.text}/></span>
                                    {correctAnswers.has(String(option.key)) && <FiCheckCircle aria-label="پاسخ صحیح"/>}
                                </div>
                            ))}
                        </div>
                    )}
                    {!!question.matching_pairs?.length && (
                        <div className="qe-matching-pairs">
                            {question.matching_pairs.map((pair, pairIndex) => <div key={pairIndex}><span><MathText text={pair.left}/></span><b>←</b><span><MathText text={pair.right}/></span></div>)}
                        </div>
                    )}
                    <details className="qe-answer-box">
                        <summary><FiChevronDown/> پاسخ و توضیح</summary>
                        <div>
                            <p><strong>پاسخ صحیح:</strong> <MathText text={resolvedAnswer || 'ثبت نشده'}/></p>
                            {question.answer_explanation && <p><strong>توضیح:</strong> <MathText text={question.answer_explanation}/></p>}
                            {!!question.rubric?.length && <div><strong>راهنمای تصحیح:</strong><ul>{question.rubric.map((item, rubricIndex) => <li key={rubricIndex}><MathText text={readableValue(item)}/></li>)}</ul></div>}
                        </div>
                    </details>
                    {(question.source_rationale || question.evidence?.page_number) && (
                        <div className="qe-source-note"><FiBookOpen/><span>{question.source_rationale || `منبع: صفحه ${question.evidence.page_number}`}</span></div>
                    )}
                </div>
            </article>
        );
    };

    const approveSession = async () => {
        if (!activeSession?.id || activeSession.status !== 'completed' || pendingApproval < 1) return;
        setBusyAction('approve');
        setMessage(null);
        try {
            const result = await questionEngineApi.approve(activeSession.id);
            const created = Number(result?.created_count) || 0;
            const existing = Number(result?.existing_count) || 0;
            const summary = created
                ? `${created} سؤال تأیید و به بانک سؤال افزوده شد.`
                : existing
                    ? 'سؤال‌های پذیرفته‌شده قبلاً به بانک سؤال افزوده شده‌اند.'
                    : 'وضعیت بانک سؤال بروزرسانی شد.';
            setMessage({type: 'success', text: summary});
            await pollStatus(activeSession.id);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyAction('');
        }
    };

    const renderClarificationInput = (clarification) => {
        const value = answers[clarification.id];
        if (clarification.kind === 'boolean') {
            return (
                <label className="qe-switch">
                    <input type="checkbox" checked={Boolean(value)} onChange={(event) => setAnswer(clarification, event.target.checked)}/>
                    <span/><strong>{Boolean(value) ? 'بله' : 'خیر'}</strong>
                </label>
            );
        }
        if (clarification.kind === 'integer') {
            return <input type="number" value={value || ''} onChange={(event) => setAnswer(clarification, event.target.value)}/>;
        }
        if (clarification.kind === 'text') {
            return <textarea rows="3" value={value || ''} onChange={(event) => setAnswer(clarification, event.target.value)}/>;
        }
        if (clarification.kind === 'multiple_choice') {
            const selected = Array.isArray(value) ? value : [];
            return (
                <div className="qe-choice-list">
                    {(clarification.options || []).map((option) => (
                        <label key={option.value} className={selected.includes(option.value) ? 'selected' : ''}>
                            <input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggleMultipleAnswer(clarification, option.value)}/>
                            <span><strong>{option.label || option.value}</strong>{option.description && <small>{option.description}</small>}</span>
                            {option.recommended && <em>پیشنهادی</em>}
                        </label>
                    ))}
                </div>
            );
        }
        return (
            <div className="qe-choice-list">
                {(clarification.options || []).map((option) => (
                    <label key={option.value} className={value === option.value ? 'selected' : ''}>
                        <input type="radio" name={clarification.id} checked={value === option.value} onChange={() => setAnswer(clarification, option.value)}/>
                        <span><strong>{option.label || option.value}</strong>{option.description && <small>{option.description}</small>}</span>
                        {option.recommended && <em>پیشنهادی</em>}
                    </label>
                ))}
                {clarification.allow_custom && (
                    <input value={(clarification.options || []).some((option) => option.value === value) ? '' : (value || '')}
                        onChange={(event) => setAnswer(clarification, event.target.value)}
                        placeholder="پاسخ دلخواه"/>
                )}
            </div>
        );
    };

    const currentStepIndex = workflowStatusIndexes[activeSession?.status] ?? -1;

    return (
        <main className={`qe-page ${showResults ? 'has-results' : ''}`}>
            <section className="qe-hero app-page-header">
                <div>
                    <span><FiHelpCircle/> تولید سؤال هوشمند</span>
                    <h1>سامانه چندمرحله‌ای طراحی سؤال</h1>
                    <p>یک منبع موجود یا فایل جدید را انتخاب کنید، روند طراحی را دنبال کنید و سؤال‌های پذیرفته‌شده را بررسی و تأیید کنید.</p>
                </div>
                <div className="qe-hero-side app-page-header-actions">
                    <div className="qe-stats">
                        <div><strong>{stats.running}</strong><small>در حال اجرا</small></div>
                        <div><strong>{stats.waiting}</strong><small>نیازمند پاسخ</small></div>
                        <div><strong>{stats.completed}</strong><small>تکمیل‌شده</small></div>
                    </div>
                    <button type="button" className="qe-new-session-button" onClick={toggleCreatePanel}>
                        {showCreate ? <FiXCircle/> : <FiPlus/>} {showCreate ? 'بستن فرم' : 'طراحی جدید'}
                    </button>
                </div>
            </section>

            {message && <div className={`qe-message ${message.type}`}>
                {message.type === 'error' ? <FiAlertCircle/> : <FiCheckCircle/>}
                <span>{message.text}</span>
            </div>}

            {showCreate && (
                <div className="qe-create-overlay" onMouseDown={(event) => {
                    if (event.target === event.currentTarget && !isCreating) setShowCreate(false);
                }}>
                <section ref={createPanelRef} className="qe-panel qe-create-panel" role="dialog" aria-modal="true" aria-labelledby="qe-create-title" tabIndex="-1">
                    <header className="qe-panel-header">
                        <div><FiUploadCloud/><div><h2 id="qe-create-title">ایجاد طراحی جدید</h2><p>منبع موجود را انتخاب کنید یا فایل تازه‌ای بارگذاری کنید.</p></div></div>
                        <button type="button" className="qe-icon-button" onClick={() => setShowCreate(false)} disabled={isCreating} aria-label="بستن پنجره ایجاد طراحی"><FiXCircle/></button>
                    </header>
                    <EngineUsageCard state={engineUsage} compact/>
                    {engineUsage.usage?.allowed === false && !engineUsage.usage?.exempt ? null : <form className="qe-create-form" onSubmit={createSession}>
                        <div className="qe-source-tabs" role="tablist" aria-label="نوع منبع طراحی سؤال">
                            <button type="button" role="tab" aria-selected={sourceMode === 'existing'}
                                className={sourceMode === 'existing' ? 'active' : ''}
                                onClick={() => { setSourceMode('existing'); setDocumentFile(null); }}>
                                <FiDatabase/> منبع موجود
                            </button>
                            <button type="button" role="tab" aria-selected={sourceMode === 'upload'}
                                className={sourceMode === 'upload' ? 'active' : ''}
                                onClick={() => { setSourceMode('upload'); setSelectedResourceId(''); }}>
                                <FiUploadCloud/> بارگذاری فایل جدید
                            </button>
                        </div>
                        {sourceMode === 'existing' ? (
                            <div className="qe-resource-picker" role="tabpanel">
                                <div className="qe-resource-search">
                                    <FiBookOpen/>
                                    <input value={resourceSearch} onChange={(event) => setResourceSearch(event.target.value)}
                                        placeholder="جستجو در نام یا موضوع منابع..." aria-label="جستجوی منبع"/>
                                    <button type="button" onClick={loadResources} disabled={resourcesLoading} aria-label="بروزرسانی منابع">
                                        <FiRefreshCw className={resourcesLoading ? 'qe-spin' : ''}/>
                                    </button>
                                </div>
                                <div className="qe-resource-options" role="listbox" aria-label="منابع موجود">
                                    {resourcesLoading && !resourcesLoaded ? (
                                        <div className="qe-resource-empty"><FiLoader className="qe-spin"/> در حال دریافت منابع...</div>
                                    ) : filteredResources.length ? filteredResources.map((resource) => {
                                        const resourceId = String(resource.resource_id);
                                        const selected = selectedResourceId === resourceId;
                                        return <button type="button" role="option" aria-selected={selected} key={resourceId}
                                            className={selected ? 'selected' : ''} onClick={() => setSelectedResourceId(resourceId)}>
                                            <span className="qe-resource-icon"><FiFileText/></span>
                                            <span><strong>{resource.title || resource.original_name || 'منبع بدون عنوان'}</strong>
                                                <small>{[resource.original_name !== resource.title ? resource.original_name : '', resource.subject_title].filter(Boolean).join(' · ') || resourceId}</small></span>
                                            {selected && <FiCheckCircle className="qe-resource-check"/>}
                                        </button>;
                                    }) : (
                                        <div className="qe-resource-empty">
                                            <FiAlertCircle/>{availableResources.length ? 'منبعی با این عبارت پیدا نشد.' : 'هنوز منبع فایل‌داری برای انتخاب وجود ندارد.'}
                                            {!availableResources.length && <button type="button" onClick={() => setSourceMode('upload')}>بارگذاری فایل جدید</button>}
                                        </div>
                                    )}
                                </div>
                                {resources.length > availableResources.length && <small className="qe-resource-hint">منابع بدون فایل برای طراحی سؤال نمایش داده نمی‌شوند.</small>}
                            </div>
                        ) : (
                            <label className="qe-dropzone" role="tabpanel">
                                <FiFileText/>
                                <strong>{documentFile ? documentFile.name : 'فایل منبع را انتخاب کنید'}</strong>
                                <span>PDF، DOCX، PPTX، TEX، TXT یا Markdown</span>
                                <input type="file" accept=".pdf,.docx,.pptx,.tex,.txt,.md"
                                    onChange={(event) => setDocumentFile(event.target.files?.[0] || null)}/>
                            </label>
                        )}
                        <div className="qe-form-grid">
                            <label>
                                <span>عنوان</span>
                                <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="اگر خالی باشد نام منبع استفاده می‌شود"/>
                            </label>
                            <label>
                                <span>زبان پرسش‌های تکمیلی</span>
                                <select value={interfaceLanguage} onChange={(event) => setInterfaceLanguage(event.target.value)}>
                                    <option value="fa">فارسی</option>
                                    <option value="en">English</option>
                                </select>
                            </label>
                            <label>
                                <span>الگوی طراحی</span>
                                <select value={preset} onChange={(event) => setPreset(event.target.value)}>
                                    <option value="balanced">متعادل — ۱۰ سؤال</option>
                                    <option value="diagnostic">تشخیصی — ۱۰ سؤال</option>
                                    <option value="summative">پایانی — ۲۰ سؤال</option>
                                    <option value="custom">سفارشی — شروع با ۱ سؤال</option>
                                </select>
                            </label>
                        </div>
                        <label className={`qe-auto-answer ${autoAnswerClarifications ? 'enabled' : ''}`}>
                            <input type="checkbox" checked={autoAnswerClarifications} onChange={(event) => setAutoAnswerClarifications(event.target.checked)}/>
                            <span className="qe-auto-answer-toggle"><i/></span>
                            <span className="qe-auto-answer-copy">
                                <strong>پاسخ‌گویی خودکار به پرسش‌های تکمیلی</strong>
                                <small>اگر پاسخ پیشنهادی معتبر باشد، سامانه آن را اعمال می‌کند تا پردازش متوقف نشود.</small>
                            </span>
                        </label>
                        {!engineUsage.loading && !canStartGeneration && engineUsage.usage?.allowed && <div className="engine-usage-warning"><FiAlertCircle/> ظرفیت فعلی برای این الگو کافی نیست.</div>}
                        <button className="qe-primary-button" disabled={isCreating || !hasSource || engineUsage.loading || !canStartGeneration}>
                            {isCreating ? <><FiLoader className="qe-spin"/> در حال ایجاد طراحی...</> : <><FiPlay/> شروع طراحی سؤال</>}
                        </button>
                    </form>}
                </section>
                </div>
            )}

            <div className="qe-workspace focused">

                <section className="qe-panel qe-sessions-panel">
                    <header className="qe-panel-header">
                        <div><FiRefreshCw/><div><h2>طراحی‌های اخیر</h2><p>فهرست فرایندهای طراحی سؤال</p></div></div>
                        <button type="button" className="qe-icon-button" onClick={() => loadSessions(false)} disabled={isLoading} aria-label="بروزرسانی فهرست طراحی‌ها">
                            <FiRefreshCw className={isLoading ? 'qe-spin' : ''}/>
                        </button>
                    </header>
                    <div className="qe-session-list">
                        {isLoading ? (
                            <div className="qe-empty"><FiLoader className="qe-spin"/><span>در حال دریافت طراحی‌ها...</span></div>
                        ) : sessions.length ? sessions.map((session) => (
                            <button key={session.id} type="button" className={activeSessionId === session.id ? 'active' : ''} onClick={() => selectSession(session.id)}>
                                <strong>{session.title || session.document?.name || session.id}</strong>
                                <span className={`qe-status ${session.status}`}>{formatStatus(session.status)}</span>
                                <small>{getSessionSubtitle(session)}</small>
                            </button>
                        )) : (
                            <div className="qe-empty"><FiFileText/><span>هنوز طراحی‌ای ایجاد نشده است.</span></div>
                        )}
                    </div>
                </section>

                <section className="qe-panel qe-detail-panel">
                    <header className="qe-panel-header">
                        <div><FiHelpCircle/><div><h2>وضعیت طراحی</h2><p>روند پردازش، پرسش‌های تکمیلی و نتیجه نهایی</p></div></div>
                        {activeSession?.id && <button type="button" className="qe-icon-button" onClick={() => pollStatus(activeSession.id)} aria-label="بروزرسانی وضعیت طراحی">
                            <FiRefreshCw/>
                        </button>}
                    </header>

                    {!activeSession ? (
                        <div className="qe-empty large"><FiFileText/><h3>یک طراحی را انتخاب کنید</h3><p>پس از ایجاد طراحی، وضعیت آن در همین بخش نمایش داده می‌شود.</p></div>
                    ) : (
                        <div className="qe-detail">
                            <div className={`qe-current-status ${activeSession.status}`}>
                                {activeSession.status === 'completed' ? <FiCheckCircle/> : activeSession.status === 'failed' ? <FiAlertCircle/> : activeSession.status === 'cancelled' ? <FiXCircle/> : activeSession.status === 'waiting_for_user' ? <FiPauseCircle/> : <FiLoader className="qe-spin"/>}
                                <div>
                                    <strong>{formatStatus(activeSession.status)}</strong>
                                    <small>{activeSession.status === 'completed'
                                        ? importedQuestions
                                            ? `${importedQuestions} سؤال در بانک سؤال ثبت شده است.`
                                            : pendingApproval
                                                ? `${pendingApproval} سؤال آماده بررسی و تأیید است.`
                                                : 'پردازش کامل شده است.'
                                        : 'وضعیت طراحی به‌صورت خودکار به‌روز می‌شود.'}</small>
                                </div>
                            </div>

                            {activeSession.status === 'completed' && busyAction === 'results' && (
                                <div className="qe-results-loading"><FiLoader className="qe-spin"/><span>در حال آماده‌سازی نمای سؤال‌ها…</span></div>
                            )}

                            {!['failed', 'cancelled'].includes(activeSession.status) && (
                                <div className="qe-stepper" aria-label="مراحل تولید سؤال">
                                    {workflowSteps.map((step, index) => (
                                        <div key={step.key} className={`${index < currentStepIndex ? 'done' : ''} ${index === currentStepIndex ? 'active' : ''}`}>
                                            <i>{index < currentStepIndex || activeSession.status === 'completed' ? <FiCheckCircle/> : index + 1}</i>
                                            <span>{step.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <details className="qe-technical-details">
                                <summary>جزئیات بیشتر</summary>
                                <div><span>مرحله فعلی</span><b>{agentLabels[activeSession.current_agent] || 'تعیین نشده'}</b></div>
                                <div><span>شماره به‌روزرسانی</span><b>{activeSession.revision || 0}</b></div>
                                <div><span>پرسش‌های تکمیلی</span><b>{activeSession.auto_answer_clarifications ? 'پاسخ‌گویی خودکار' : 'با تأیید شما'}</b></div>
                            </details>

                            <div className="qe-document-box">
                                <FiFileText/>
                                <div>
                                    <strong>{activeSession.document?.name || activeSession.title}</strong>
                                    <small>وضعیت استخراج: {extractionStatusLabels[activeSession.document?.extraction_status] || 'نامشخص'}</small>
                                    {!!activeSession.document?.warnings?.length && (
                                        <ul className="qe-document-warnings">
                                            {activeSession.document.warnings.map((warning, index) => <li key={index}>{formatWarning(warning)}</li>)}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            <div className="qe-progress-grid">
                                <div><span>برنامه‌ریزی‌شده</span><strong>{progress.planned || 0}</strong></div>
                                <div><span>تولیدشده</span><strong>{progress.generated || 0}</strong></div>
                                <div><span>پذیرفته‌شده</span><strong>{progress.accepted || 0}</strong></div>
                                <div><span>ردشده</span><strong>{progress.rejected || 0}</strong></div>
                            </div>

                            {activeSession.status === 'completed' && (
                                <div className="qe-bank-status">
                                    <FiDatabase/>
                                    <div>
                                        <strong>انتقال به بانک سؤال</strong>
                                        <small>{pendingApproval
                                            ? `${pendingApproval} سؤال پذیرفته‌شده منتظر تأیید شماست.`
                                            : importedQuestions
                                                ? `${importedQuestions} سؤال به بانک سؤال افزوده شده است.`
                                                : 'سؤال پذیرفته‌شده‌ای برای انتقال وجود ندارد.'}</small>
                                    </div>
                                </div>
                            )}

                            {getSessionErrorText(activeSession) && (
                                <div className="qe-error-box"><FiAlertCircle/><span>{getSessionErrorText(activeSession)}</span></div>
                            )}

                            {!!openClarifications.length && (
                                <form className="qe-clarifications" onSubmit={submitAnswers}>
                                    {activeSession.auto_answer_clarifications && (
                                        <div className="qe-auto-answer-notice"><FiAlertCircle/><span>برای این موارد پاسخ پیشنهادی معتبر کافی نبود؛ پاسخ شما برای ادامه لازم است.</span></div>
                                    )}
                                    <div className="qe-section-title">
                                        <strong>{openClarifications.length} سؤال تکمیلی</strong>
                                        <small>برای ادامه پردازش، موارد زیر را تکمیل کنید.</small>
                                    </div>
                                    {openClarifications.map((clarification) => (
                                        <article key={clarification.id} className="qe-clarification-card">
                                            <div className="qe-clarification-head">
                                                <div>
                                                    <strong>{clarification.prompt}</strong>
                                                    {clarification.help_text && <small>{clarification.help_text}</small>}
                                                </div>
                                                <span>{clarificationTypeLabels[clarification.kind] || 'پاسخ انتخابی'}</span>
                                            </div>
                                            {renderClarificationInput(clarification)}
                                            <label className="qe-remember">
                                                <input type="checkbox" checked={rememberIds.includes(clarification.id)} onChange={() => toggleRemember(clarification.id)}/>
                                                ذخیره به‌عنوان ترجیح پیش‌فرض
                                            </label>
                                        </article>
                                    ))}
                                    <button className="qe-primary-button" disabled={busyAction === 'answers'}>
                                        {busyAction === 'answers' ? <><FiLoader className="qe-spin"/> در حال ارسال...</> : <><FiSend/> ثبت پاسخ‌ها</>}
                                    </button>
                                </form>
                            )}

                            <div className="qe-actions">
                                {!terminalStatuses.includes(activeSession.status) && <button type="button" onClick={() => runSessionAction('cancel', questionEngineApi.cancel, 'درخواست لغو پردازش ارسال شد.')} disabled={busyAction === 'cancel'}>
                                    {busyAction === 'cancel' ? <FiLoader className="qe-spin"/> : <FiXCircle/>} لغو
                                </button>}
                                {activeSession.status === 'failed' && <button type="button" onClick={() => runSessionAction('retry', questionEngineApi.retry, 'درخواست تلاش مجدد ارسال شد.')} disabled={busyAction === 'retry'}>
                                    {busyAction === 'retry' ? <FiLoader className="qe-spin"/> : <FiRotateCcw/>} تلاش دوباره
                                </button>}
                                {activeSession.status === 'completed' && <button type="button" className="primary" onClick={loadResults} disabled={busyAction === 'results'}>
                                    {busyAction === 'results' ? <FiLoader className="qe-spin"/> : <FiEye/>} مشاهده سؤال‌ها
                                </button>}
                                {activeSession.status === 'completed' && pendingApproval > 0 && <button type="button" className="approve" onClick={approveSession}
                                    disabled={busyAction === 'approve'}>
                                    {busyAction === 'approve' ? <FiLoader className="qe-spin"/> : <FiDatabase/>}
                                    تأیید و افزودن {pendingApproval} سؤال به بانک
                                </button>}
                            </div>
                        </div>
                    )}
                </section>
            </div>

            {showResults && activeSession?.status === 'completed' && (
                <section ref={resultsRef} className="qe-panel qe-results-panel">
                    <header className="qe-results-header">
                        <div>
                            <span><FiCheckCircle/> نتیجه آماده است</span>
                            <h2>سؤال‌های پذیرفته‌شده</h2>
                            <p>{sessionResult?.questions?.length || 0} سؤال پس از داوری کیفیت برای بررسی شما آماده شده است.</p>
                        </div>
                        <button type="button" onClick={() => setShowResults(false)}><FiXCircle/> بستن</button>
                    </header>
                    <div className="qe-results-summary">
                        <div><strong>{sessionResult?.questions?.length || 0}</strong><span>سؤال پذیرفته‌شده</span></div>
                        <div><strong>{sessionResult?.rejected_count || 0}</strong><span>سؤال ردشده</span></div>
                        <div><strong>{sessionResult?.questions?.reduce((total, item) => total + (Number(item.marks) || 0), 0) || 0}</strong><span>مجموع نمره</span></div>
                    </div>
                    <div className="qe-question-list">
                        {sessionResult?.questions?.length
                            ? sessionResult.questions.map(renderQuestion)
                            : <div className="qe-empty"><FiFileText/><span>سؤال پذیرفته‌شده‌ای برای نمایش وجود ندارد.</span></div>}
                    </div>
                </section>
            )}
        </main>
    );
};

export default QuestionEngine;
